import { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useLookups } from '../../../../hooks/useLookups';
import * as uiSlice from '../../../../data/ui.slice';
import * as clientSlice from '../../../../data/client.slice';
import * as lookupSlice from '../../../../data/lookup.slice';
import clientUtils from '../../../../services/clientUtils';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import {
  ClientSetting,
  ClientSettingsOption,
} from '../../../../data/models/client-settings.model';
import TextInput from '../../../forms/TextInput';
const PREFIX = 'ClientPreferencesDialog';
const classes = {
  content: `${PREFIX}-content`,
  control: `${PREFIX}-control`,
  actions: `${PREFIX}-actions`,
};
const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    paddingTop: `${theme.spacing(2)} !important`,
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.control}`]: {
    width: 320,
  },
  [`& .${classes.actions}`]: {
    gap: theme.spacing(1),
  },
}));
interface ClientPreferencesDialogProps {
  clientBC: string;
  open: boolean;
  preferences: ClientSetting[] | null;
  close: () => void;
}
export default function ClientPreferencesDialog({
  clientBC,
  open,
  preferences,
  close,
}: ClientPreferencesDialogProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');
  const dispatch = useAppDispatch();
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const savingConfig = useAppSelector(clientSlice.selectSavingClientSettings);
  useLookups(['clientSettingOption']);
  const clientSettingOptions = useAppSelector(
    lookupSlice.selectClientSettingOptions,
  );
  const getDefaultPreferences = () => {
    return {
      DefaultPickup: clientUtils.getPickUpTimeOffsetMinutes(
        clientSettingOptions,
        preferences,
      ),
    };
  };
  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: getDefaultPreferences(),
  });
  useEffect(() => {
    if (preferences) {
      formHook.reset(getDefaultPreferences());
    }
  }, [preferences]);
  useEffect(() => {
    if (saveSuccessMessage) {
      handleClose();
    }
  }, [saveSuccessMessage]);
  const handleClose = () => {
    formHook.reset(getDefaultPreferences());
    close();
  };
  const handleSave = (data: any) => {
    const currentSetting = (preferences || []).find(
      (p) =>
        p.clientSettingOptionBC ===
        ClientSettingsOption.PickupTimeOffsetMinutes,
    );
    const setting = clientUtils.getPickupTimeOffsetSettingsFromData(
      clientSettingOptions,
      data,
      currentSetting?.clientSettingID,
    );
    dispatch(clientSlice.saveClientSettingsAsync(clientBC, [setting]));
  };
  const handleKeyDown = (evt: any) => {
    if (evt.key >= '0' && evt.key <= '9') {
      if (evt.target.value.length > 2) evt.preventDefault();
    }
  };
  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle>{t('client.editPreferences')}</DialogTitle>
      <DialogContent className={classes.content}>
        <TextInput
          name="DefaultPickup"
          label={`${t('client.defaultPickupTime')} (${t('client.priorToAppointment')})`}
          className={classes.control}
          control={formHook.control}
          type="number"
          required={true}
          inputProps={{
            min: 1,
            max: 999,
            onKeyDown: handleKeyDown,
          }}
          rules={{ required: requiredFieldMessage }}
        />
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!formHook.formState.isValid || savingConfig}
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.ok')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
